import { Stepper } from "@shared/components/Stepper";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConsumerDetails } from "../../hooks";
import { AddressDetailsStep } from "./stepComponents/AddressDetailsStep";
import { ApplicationReviewForm } from "./stepComponents/ApplicationReviewStep";
import { ConsumerDetailsStep } from "./stepComponents/ConsumerDetailsStep";
import { DocumentUploadStep } from "./stepComponents/DocumentUploadStep";
import { PlanSelectionForm } from "./stepComponents/PlanSelectionStep";

export const ManualApplicationStepper = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const returnPath = "/cx/consumer-management/create";
  const { consumerId } = useParams<{ consumerId: string }>();

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Consumer Service Application");

  // Generate unique storage key - include edit mode and consumer ID if editing
  const storageKey = consumerId
    ? `edit-application-progress-${consumerId}`
    : `new-application-progress`;

  // State to track if edit data has been prepared
  const [isEditDataPrepared, setIsEditDataPrepared] = useState(!consumerId); // true for create mode, false for edit mode
  const [isDataTransformed, setIsDataTransformed] = useState(!consumerId); // Additional state for transformation completion

  // Fetch consumer details only if consumerId exists (edit mode)
  const { data: consumerDetailsResponse, isLoading: isLoadingConsumerDetails } =
    useConsumerDetails(
      {
        consumer_id: consumerId?.toString() || "",
        remote_utility_id: remoteUtilityId,
        is_detail_info: true,
        is_document: true,
      },
      {
        enabled: !!consumerId && !!remoteUtilityId,
      }
    );

  const consumerDetails = consumerDetailsResponse?.result;

  useEffect(() => {
    startTracking();
  }, [startTracking]);

  // Also add this to the handleStepChange function
  const handleStepChange = (stepIndex, previousStepIndex, stepContext) => {
    console.log(
      `🔄 Step changed from ${previousStepIndex} to ${stepIndex}`,
      stepContext
    );
  };

  // Transform consumer details to stepper format and save using stepHelpers
  useEffect(() => {
    if (consumerId && consumerDetails && !isLoadingConsumerDetails) {
      console.log("Consumer details loaded:", consumerDetails);

      // Set loading state for transformation
      setIsDataTransformed(false);

      // Transform API response to stepper format
      const transformedStepData =
        transformConsumerDetailsToStepData(consumerDetails);

      // Save using stepHelpers pattern - create a proper stepper storage format

     

      try {
        const stepperData = {
          currentStep: 0, // Start from the beginning for editing
          completedSteps: transformedStepData.completedSteps,
          skippedSteps: transformedStepData.skippedSteps,
          stepData: transformedStepData.stepData,
          timestamp: Date.now(),
          version: "1.0.0",
          isEditMode: true,
          originalConsumerId: consumerDetails.id,
        };

        // Clear any existing data first
        localStorage.removeItem(storageKey);

        // Save the new data immediately
        localStorage.setItem(storageKey, JSON.stringify(stepperData));
        console.log(
          "Consumer details transformed and saved using stepHelpers format:",
          stepperData
        );
        console.log(
          "🔍 Saving stepData with keys:",
          Object.keys(stepperData.stepData)
        );
        console.log("🔍 Step 1 data being saved:", stepperData.stepData["1"]);

        // Mark as ready immediately - no delays
        setIsEditDataPrepared(true);
        setIsDataTransformed(true);
        console.log(
          "✅ Edit data prepared and saved to localStorage with key:",
          storageKey
        );
        console.log("📋 Saved data structure:", stepperData);
      } catch (error) {
        console.error("Failed to save transformed consumer details:", error);
        // Even on error, allow component to proceed to avoid infinite loading
        setIsEditDataPrepared(true);
        setIsDataTransformed(true);
      }
    }
  }, [consumerId, consumerDetails, isLoadingConsumerDetails, storageKey]);

  // Transform function
  const transformConsumerDetailsToStepData = (details: any) => {
    console.log(
      "🔄 Transforming consumer details to step data:",
      JSON.stringify(details, null, 2)
    );
    const now = new Date().toISOString();
      const fallbackPayload = JSON.parse(
        localStorage.getItem("consumerFirstStepData") || "{}"
      );
      console.log("Fallback payload from localStorage:", fallbackPayload);
      const fallbackPrimary = fallbackPayload?.consumer_details?.primaryConsumer || {};
      const fallbackSecondary = fallbackPayload?.consumer_details?.secondaryConsumer || {};

    // Step 0: Consumer Details
    const step0Data = {
      firstName: details.firstName || fallbackPrimary.firstName || "",
      lastName: details.lastName || fallbackPrimary.lastName ||"",
      phoneNumber: details.contactNumber || fallbackPrimary.contactNumber ||"",
      email: details.email || fallbackPrimary.email || "",
      category: details.category || fallbackPrimary.category || "",
      categoryDisplay: details.categoryDisplay || fallbackPrimary.categoryDisplay || "",
      subCategory: details.subCategory || fallbackPrimary.subCategory ||"",
      subCategoryDisplay: details.subCategoryDisplay || fallbackPrimary.subCategoryDisplay || "",
      isVip: details.isVip || fallbackPrimary.isVip || false,
      vipLevel: "",
      hasSecondary: !!details.additionalData?.secondaryConsumer || !!fallbackSecondary,
      secondaryFirstName:
        details.additionalData?.secondaryConsumer?.firstName || fallbackSecondary.firstName ||"",
      secondaryLastName:
        details.additionalData?.secondaryConsumer?.lastName ||  fallbackSecondary.lastName ||"",
      secondaryPhoneNumber:
        details.additionalData?.secondaryConsumer?.contactNumber || fallbackSecondary.contactNumber || "",
      secondaryEmail: details.additionalData?.secondaryConsumer?.email || fallbackSecondary.email || "",
      secondaryRelationship:
        details.additionalData?.secondaryConsumer?.relationship || fallbackSecondary.secondaryRelationship ||"",
      emailVerified: true, // Reset verification status
      secondaryEmailVerified: true,
      lastUpdated: now,
      timestamp: now,
      stepIndex: 0,
    };

    // Step 1: Address Details
    const addressMap = details.territoryData;
    console.log("Address map:", addressMap);
    const step1Data = {
      houseNumber: details.addressData?.service?.UNIT || "",
      streetAddress: details.addressData?.service?.ADDRESS || "",
      premiseName: [details.addressData?.service?.PREMISE],
      city: addressMap?.service?.county || "",
      area: addressMap?.service?.area || "",
      subArea: addressMap?.service?.subArea || "",
      zipCode: details.addressData?.service?.ZIPCODE || "",
      premiseNameDisplay: addressMap?.service?.premise || "",
      sameAsBilling:
        !addressMap?.billing ||
        (details.addressData?.service?.UNIT ===
          details.addressData?.billing?.UNIT &&
          details.addressData?.service?.ADDRESS ===
            details.addressData?.billing?.ADDRESS),
      billingHouseNumber: details.addressData?.billing?.UNIT || "",
      billingStreetAddress: details.addressData?.billing?.ADDRESS || "",
      billingCity: addressMap?.billing?.county || "",
      billingArea: addressMap?.billing?.area || "",
      billingSubArea: addressMap?.billing?.subArea || "",
      billingPremiseName: [details.addressData?.billing?.PREMISE],
      billingZipCode: details.addressData?.service?.ZIPCODE || "",
      billingPremiseNameDisplay: details?.billing?.premise || "",
      serviceAvailable: true, // Assume available since consumer exists
      timestamp: now,
    };
    console.log("Step 1 data:", step1Data);

    // Step 2: Document Upload - transform existing documents from API
    let step2Data = {
      documents: [], // Will be populated from API documents
      fileObjects: [],
      fileMetadata: [],
      isStepComplete: false,
      timestamp: now,
      stepIndex: 2,
      notes: "Documents need to be re-uploaded in edit mode",
      incidentDate: "",
      expectedResolution: "",
    };

    // Transform existing documents from API
    if (
      details.document &&
      Array.isArray(details.document) &&
      details.document.length > 0
    ) {
      console.log("📄 Transforming documents from API:", details.document);

      step2Data.documents = details.document.map((doc: any, index: number) => ({
        id: doc.id?.toString() || `doc-${index}`,
        name:
          doc.documentTypeName || doc.documentType || `Document ${index + 1}`,
        type: "application/pdf", // Default type, could be extracted from file URL
        category: doc.documentTypeName || doc.documentType || "",
        subCategory: doc.documentSubtypeName || doc.documentSubtype || "",
        categoryCode: doc.documentType || "",
        subCategoryCode: doc.documentSubtype || "",
        size: 0, // Unknown from API
        progress: 100, // Already uploaded
        status: "completed" as const,
        url: doc.file || "",
        file: undefined, // No File object from API
        needsReupload: false, // Already uploaded
        consumerRole: doc.consumerRole || "Primary",
        isActive: doc.isActive !== false,
        documentStatus: doc.documentStatus || "Pending",
      }));

      step2Data.fileMetadata = step2Data.documents.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        category: doc.category,
        subCategory: doc.subCategory,
        categoryCode: doc.categoryCode,
        subCategoryCode: doc.subCategoryCode,
        size: doc.size,
        needsReupload: false,
        url: doc.url,
      }));

      // Mark as complete if there are documents
      step2Data.isStepComplete = step2Data.documents.length > 0;
      console.log("📄 Documents transformed:", step2Data.documents);
    }

    // Step 3: Plan Selection
    // const step3Data = details ? {
    //   selectedPlan: {
    //     id: details.remotePlanId,
    //     planName: "Selected Plan", // We don't have plan details, will be loaded dynamically
    //     planId: details.remotePlanId
    //   },

    //   planId: details.remotePlanId,
    //   timestamp: now,
    //   stepIndex: 3
    // } : {
    //   timestamp: now,
    //   stepIndex: 3
    // };

    // Step 3: Plan Selection
    const step3Data = details
      ? {
          selectedPlan: {
            id: details.remotePlanId,
            planName: details.planName || "Selected Plan", // fallback
            planId: details.remotePlanId,
          },
          planId: details.remotePlanId,
          planName: details.planName || "",
          category: details.category || "",
          subcategory: details.subcategory || "",
          description: details.description || "",
          billingCycle: details.billingCycle || "",
          billingFrequency: details.billingFrequency || "",
          billingFrequencyDisplay: details.billingFrequencyDisplay || "",
          utilityServices: details.utilityServices || [],
          utilityServiceData: details.utilityServiceData || [],
          processingFees: details.processingFees || 0,
          connectionFee: details.connectionFee || 0,
          securityDeposit: details.securityDeposit || 0,
          tax: details.tax || 0,
          taxType: details.taxType || "",
          taxTypeDisplay: details.taxTypeDisplay || "",
          validity: details.validity || "",
          total: details.total || 0,
          timestamp: now,
          stepIndex: 3,
        }
      : {
          timestamp: now,
          stepIndex: 3,
        };

    // Determine completed steps based on available data
    const completedSteps = [];
    if (step0Data.firstName && step0Data.lastName && step0Data.email) {
      completedSteps.push(0);
    }
    if (step1Data.houseNumber && step1Data.streetAddress && step1Data.city) {
      completedSteps.push(1);
    }
    // Mark step 2 as completed if there are existing documents
    if (step2Data.documents && step2Data.documents.length > 0) {
      completedSteps.push(2);
    }
    if (details.remotePlanId) {
      completedSteps.push(3);
    }

    console.log("✅ Transform complete. Step data created:", {
      step0: !!step0Data.firstName,
      step1: !!step1Data.houseNumber,
      step2: `${step2Data.documents?.length || 0} documents`,
      step3: !!step3Data.planId,
    });

    return {
      completedSteps,
      skippedSteps: [],
      stepData: {
        "0": step0Data,
        "1": step1Data,
        "2": step2Data,
        "3": step3Data,
      },
      isEditMode: true,
      originalConsumerId: details.id,
    };
  };

  // Determine if we should show the stepper
  const shouldShowStepper = useMemo(() => {
    // For create mode (no consumerId), always show stepper
    if (!consumerId) {
      return true;
    }

    // For edit mode, only show stepper when:
    // 1. Not loading consumer details
    // 2. Edit data has been prepared
    // 3. Data transformation is complete
    return !isLoadingConsumerDetails && isEditDataPrepared && isDataTransformed;
  }, [
    consumerId,
    isLoadingConsumerDetails,
    isEditDataPrepared,
    isDataTransformed,
  ]);

  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Consumer Details",
        slug: "consumer-details",
        component: (
          <ConsumerDetailsStep
            storageKey={storageKey}
            logUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Consumer Information Help",
        infoDescription:
          "Enter the consumer's personal information and contact details. This information will be used for account verification and communication. All fields marked with an asterisk (*) are required.",
      },
      {
        title: "Address Details",
        slug: "address-details",
        component: (
          <AddressDetailsStep
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Address Information Help",
      },
      {
        title: "Document Upload",
        slug: "document-upload",
        component: (
          <DocumentUploadStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            isEditMode={!!consumerId}
            consumerId={consumerId}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Document Upload Help",
        infoDescription:
          "Upload required identity and proof documents to verify the applicant's identity and eligibility. All documents must be clear and legible. Accepted formats: JPG, PNG, PDF (max 5MB each).",
      },
      {
        title: "Plan Selection",
        slug: "plan-selection",
        component: (
          <PlanSelectionForm
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Plan Selection Help",
        infoDescription:
          "Select a service plan that best fits the consumer's needs and usage patterns. All plans include standard service features, with premium options available for additional benefits.",
      },
      {
        title: "Review",
        slug: "review",
        component: (
          <ApplicationReviewForm
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
            stopTracking={stopTracking}
          />
        ),
        optional: false,
        infoTitle: "Application Review Help",
        infoDescription:
          "Review all provided information for accuracy before submitting the application. Make sure all required information is provided and correct.",
      },
    ],
    [storageKey, remoteUtilityId, consumerId]
  );

  // const handleStepChange = (stepIndex, previousStepIndex, stepContext) => {
  //   // console.log(`Step changed from ${previousStepIndex} to ${stepIndex}`, stepContext);
  // };

  const handleComplete = (allStepData, completedSteps, skippedSteps) => {
    stopTracking();
    console.log("Application completed!", {
      data: allStepData,
      completed: completedSteps,
      skipped: skippedSteps,
    });

    // Navigate to success page or handle completion
    // navigate('/applications/success', {
    //   state: {
    //     applicationData: allStepData,
    //     applicationId: `APP-${Math.floor(10000 + Math.random() * 90000)}`
    //   }
    // });
  };

  // Show loading state when not ready to render stepper
  if (!shouldShowStepper) {
    return (
      <div className="">
        <div className="mb-6 flex justify-between items-center">
          <div>
            {/* <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {consumerId
                ? consumerDetails
                  ? `Edit Consumer Application #${consumerDetails.consumerNo}`
                  : 'Loading Consumer Application...'
                : 'Loading Application...'}
            </h1> */}
            <p className="text-gray-600">
              {consumerId
                ? "Loading and preparing consumer details..."
                : "Preparing application..."}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(returnPath)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {consumerId ? `Edit Consumer Application #${consumerDetails.consumerNo}` : 'New Consumer Application'}
          </h1>
          <p className="text-gray-600">
            {consumerId 
              ? 'Edit the consumer application by updating the required steps.'
              : 'Create a new consumer application by completing all required steps.'
            }
          </p>
          {consumerId && consumerDetails && (
            <div className="mt-2 text-sm text-blue-600">
              Editing: {consumerDetails.firstName} {consumerDetails.lastName} ({consumerDetails.email})
            </div>
          )}
        </div> */}
      <Button
        className="float-right"
        variant="outline"
        onClick={() => navigate(returnPath)}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      <Stepper
        steps={steps}
        onStepChange={() => {}}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // console.log(`Saving step ${stepIndex}:`, stepData);
        }}
        enableApiSave={false}
        saveOnStepChange={true}
        trackDirtyState={false}
        autoSave={true}
        persistProgress={true}
        allowSkip={false} // All steps are required
        storageKey={storageKey}
        enableRouting={true} // Enable hash-based routing
        onComplete={() => {}}
        showDraftResume={true}
        enableDraftMode={true}
        onValidation={undefined}
      />
    </div>
  );
};
